import { LEADERBOARD_LIMIT } from "../game/leaderboard";
import { sanitizeProfileName } from "../game/profileStorage";
import type { LeaderboardEntry, RankedLeaderboardEntry } from "../lib/leaderboard";

const FALLBACK_ROW_HEIGHT_PX = 56;

export type DisplayLeaderboardRow = {
  key: string;
  entryId: string | null;
  rank: number;
  name: string;
  value: number | null;
  isCurrentPlayer: boolean;
  showChangeNameButton: boolean;
  isPlaceholder?: boolean;
};

type BuildLeaderboardRowsOptions = {
  entries: readonly LeaderboardEntry[];
  highlightedEntry: RankedLeaderboardEntry | null;
  currentUserId: string | null;
  currentProfileName: string;
  limit: number;
  allowChangeNameButton?: boolean;
  highlightedEntryMode?: "outside-top-only" | "always";
};

export function getLeaderboardErrorMessage(error: unknown, fallbackMessage: string): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return fallbackMessage;
}

export function shouldUseProvisionalLeaderboardEntry(
  isSavingScore: boolean,
  leaderboardSaveError: string | null
): boolean {
  return isSavingScore && leaderboardSaveError === null;
}

export function formatLeaderboardMetricValue(value: number | null): string {
  if (value === null) {
    return "---";
  }

  return value.toLocaleString();
}

export function isLeaderboardEntryVisibleInSlice(
  entries: readonly LeaderboardEntry[],
  entryId: string | null,
  limit: number
): boolean {
  if (entryId === null) {
    return false;
  }

  const normalizedLimit = Math.max(1, Math.trunc(limit));
  return entries.slice(0, normalizedLimit).some((entry) => entry.id === entryId);
}

export function estimateLeaderboardEntryRank(
  entries: readonly LeaderboardEntry[],
  entry: LeaderboardEntry
): number {
  const insertionIndex = entries.findIndex((candidate) => {
    if (entry.metricValue > candidate.metricValue) {
      return true;
    }

    if (entry.metricValue < candidate.metricValue) {
      return false;
    }

    if (entry.createdAt < candidate.createdAt) {
      return true;
    }

    if (entry.createdAt > candidate.createdAt) {
      return false;
    }

    return entry.id < candidate.id;
  });

  return insertionIndex === -1 ? entries.length + 1 : insertionIndex + 1;
}

function getLeaderboardDisplayName(
  entry: LeaderboardEntry,
  currentUserId: string | null,
  currentProfileName: string
): string {
  const localProfileName = sanitizeProfileName(currentProfileName);
  if (entry.userId === currentUserId) {
    return localProfileName.length > 0 ? localProfileName : "You";
  }

  return entry.playerName.length > 0 ? entry.playerName : "Anonymous";
}

export function buildLeaderboardRows({
  entries,
  highlightedEntry,
  currentUserId,
  currentProfileName,
  limit,
  allowChangeNameButton = true,
  highlightedEntryMode = "outside-top-only"
}: BuildLeaderboardRowsOptions): DisplayLeaderboardRow[] {
  const normalizedLimit = Math.max(1, Math.trunc(limit));
  let changeNameButtonAssigned = false;
  const shouldOfferNameChange =
    allowChangeNameButton && sanitizeProfileName(currentProfileName).length === 0;
  const shouldShowChangeNameButton = (entry: LeaderboardEntry): boolean => {
    if (!shouldOfferNameChange || changeNameButtonAssigned || entry.userId !== currentUserId) {
      return false;
    }

    changeNameButtonAssigned = true;
    return true;
  };

  const topRows = entries.slice(0, normalizedLimit).map((entry, index) => ({
    key: entry.id,
    entryId: entry.id,
    rank: index + 1,
    name: getLeaderboardDisplayName(entry, currentUserId, currentProfileName),
    value: entry.metricValue,
    isCurrentPlayer:
      highlightedEntryMode === "always" ? false : entry.id === highlightedEntry?.id,
    showChangeNameButton: shouldShowChangeNameButton(entry)
  }));

  const shouldAppendHighlightedEntry =
    highlightedEntry !== null &&
    (highlightedEntryMode === "always" || highlightedEntry.rank > normalizedLimit) &&
    (highlightedEntryMode === "always" || !topRows.some((row) => row.key === highlightedEntry.id));

  if (shouldAppendHighlightedEntry && highlightedEntry !== null) {
    topRows.push({
      key:
        highlightedEntryMode === "always"
          ? `${highlightedEntry.id}-latest`
          : highlightedEntry.id,
      entryId: highlightedEntry.id,
      rank: highlightedEntry.rank,
      name: getLeaderboardDisplayName(highlightedEntry, currentUserId, currentProfileName),
      value: highlightedEntry.metricValue,
      isCurrentPlayer: true,
      showChangeNameButton: shouldShowChangeNameButton(highlightedEntry)
    });
  }

  return topRows;
}

export function fillLeaderboardRows(
  rows: readonly DisplayLeaderboardRow[],
  limit: number
): DisplayLeaderboardRow[] {
  const normalizedLimit = Math.max(1, Math.trunc(limit));
  if (rows.length >= normalizedLimit) {
    return [...rows];
  }

  const placeholderRows = Array.from(
    { length: normalizedLimit - rows.length },
    (_, index): DisplayLeaderboardRow => ({
      key: `placeholder-${rows.length + index}`,
      entryId: null,
      rank: rows.length + index + 1,
      name: "---",
      value: null,
      isCurrentPlayer: false,
      showChangeNameButton: false,
      isPlaceholder: true
    })
  );

  return [...rows, ...placeholderRows];
}

export function findRankedLeaderboardEntry(
  entries: readonly LeaderboardEntry[],
  entryId: string | null
): RankedLeaderboardEntry | null {
  if (entryId === null) {
    return null;
  }

  const entryIndex = entries.findIndex((entry) => entry.id === entryId);
  if (entryIndex === -1) {
    return null;
  }

  return {
    ...entries[entryIndex],
    rank: entryIndex + 1
  };
}

export function calculateLeaderboardPreviewLimit(
  availableHeight: number,
  rowHeight: number,
  maxVisibleRows = LEADERBOARD_LIMIT
): number {
  const normalizedMaxVisibleRows = Math.max(1, Math.trunc(maxVisibleRows));
  const normalizedRowHeight =
    Number.isFinite(rowHeight) && rowHeight > 0 ? rowHeight : FALLBACK_ROW_HEIGHT_PX;
  const normalizedAvailableHeight =
    Number.isFinite(availableHeight) && availableHeight > 0 ? availableHeight : normalizedRowHeight;

  return Math.min(
    normalizedMaxVisibleRows,
    Math.max(1, Math.floor(normalizedAvailableHeight / normalizedRowHeight))
  );
}

export function extendLeaderboardRowBottoms(
  rowBottoms: readonly number[],
  rowHeight: number,
  rowGap: number,
  targetCount: number
): number[] {
  const normalizedTargetCount = Math.max(0, Math.trunc(targetCount));
  const normalizedRowBottoms = rowBottoms
    .filter((bottom): bottom is number => Number.isFinite(bottom) && bottom > 0)
    .slice(0, normalizedTargetCount);

  if (
    normalizedTargetCount === 0 ||
    normalizedRowBottoms.length === 0 ||
    normalizedRowBottoms.length >= normalizedTargetCount
  ) {
    return normalizedRowBottoms;
  }

  const normalizedRowHeight = Number.isFinite(rowHeight) ? Math.max(rowHeight, 0) : 0;
  const normalizedRowGap = Number.isFinite(rowGap) ? Math.max(rowGap, 0) : 0;
  const measuredStrides = normalizedRowBottoms
    .slice(1)
    .map((bottom, index) => bottom - normalizedRowBottoms[index])
    .filter((stride): stride is number => Number.isFinite(stride) && stride > 0);
  const inferredStride =
    measuredStrides.length > 0
      ? measuredStrides.reduce((sum, stride) => sum + stride, 0) / measuredStrides.length
      : normalizedRowHeight + normalizedRowGap;

  if (!(inferredStride > 0)) {
    return normalizedRowBottoms;
  }

  const extendedRowBottoms = [...normalizedRowBottoms];
  while (extendedRowBottoms.length < normalizedTargetCount) {
    extendedRowBottoms.push(extendedRowBottoms[extendedRowBottoms.length - 1] + inferredStride);
  }

  return extendedRowBottoms;
}

type PreviewLimitFromRowBottomsOptions = {
  maxVisibleRows?: number;
  dedicatedTrailingRowHeight?: number;
  rowGap?: number;
};

export function calculateLeaderboardPreviewLimitFromRowBottoms(
  availableHeight: number,
  rowBottoms: readonly number[],
  {
    maxVisibleRows = LEADERBOARD_LIMIT,
    dedicatedTrailingRowHeight,
    rowGap = 0
  }: PreviewLimitFromRowBottomsOptions = {}
): number {
  const normalizedMaxVisibleRows = Math.max(1, Math.trunc(maxVisibleRows));
  const normalizedAvailableHeight =
    Number.isFinite(availableHeight) && availableHeight > 0 ? availableHeight : 0;
  const normalizedRowBottoms = rowBottoms
    .filter((bottom): bottom is number => Number.isFinite(bottom) && bottom > 0)
    .slice(0, normalizedMaxVisibleRows);
  const normalizedDedicatedRowHeight =
    Number.isFinite(dedicatedTrailingRowHeight) && dedicatedTrailingRowHeight !== undefined
      ? Math.max(dedicatedTrailingRowHeight, 0)
      : null;
  const normalizedRowGap = Number.isFinite(rowGap) ? Math.max(rowGap, 0) : 0;
  const comparisonSlackPx = 0.5;

  if (normalizedDedicatedRowHeight !== null) {
    let visibleRows =
      normalizedDedicatedRowHeight <= normalizedAvailableHeight + comparisonSlackPx ? 1 : 0;

    for (
      let topRowCount = 1;
      topRowCount <= normalizedRowBottoms.length && topRowCount < normalizedMaxVisibleRows;
      topRowCount += 1
    ) {
      const totalHeight =
        normalizedRowBottoms[topRowCount - 1] + normalizedRowGap + normalizedDedicatedRowHeight;

      if (totalHeight > normalizedAvailableHeight + comparisonSlackPx) {
        break;
      }

      visibleRows = topRowCount + 1;
    }

    return Math.min(normalizedMaxVisibleRows, Math.max(1, visibleRows));
  }

  let visibleRows = 0;
  for (let index = 0; index < normalizedRowBottoms.length; index += 1) {
    if (normalizedRowBottoms[index] > normalizedAvailableHeight + comparisonSlackPx) {
      break;
    }

    visibleRows = index + 1;
  }

  return Math.min(normalizedMaxVisibleRows, Math.max(1, visibleRows));
}
